<template>
  <div
    class="ui-input__wrap"
    :class="{
      'ui-input__wrap--error': errorMode === 'background' && error
    }">
    <div
      v-if="outerLabel"
      class="outer-input__label">
      {{ outerLabel }}
    </div>
    <label
      ref="wrapper"
      class="ui-input__wrap-inner">
      <div
        :class="{
          'ui-input--has-title': title.length,
          'ui-input--has-suffix': textAppendix.suffix,
          'ui-input--has-error': error,
        }"
        class="ui-input__input-wrap">
        <slot name="prefix" />

        <div
          v-if="textAppendix.prefix && (((title.length && hasValue) || !title.length) || hasFocus)"
          class="ui-input__prefix transition--color">
          {{ textAppendix.prefix }}
        </div>

        <div
          v-if="inputIcon.path"
          class="ui-input__icon-wrap">
          <div
            :style="{
              '--icon-path': `url(${inputIcon.path})`
            }"
            class="ui-input__icon-image" />
        </div>

        <div class="ui-input__body">
          <input
            ref="input"
            :value="val"
            :type="type"
            :placeholder="placeholder"
            :maxlength="maxlength"
            :disabled="disabled"
            :tabindex="tabindex"
            :name="name"
            :inputmode="inputmode"
            :step="step"
            :autocomplete="autocomplete"
            :accept="accept"
            class="ui-input__input"
            @focus="onFocus"
            @blur="onBlur"
            @keypress="onKeyPress"
            @paste="onPaste"
            @input="setValue"
            @keyup="$emit('keyup', $event)"
            @change="handleChange">

          <div
            v-if="title"
            :class="{ 'ui-input__title-wrap--active': isActive }"
            class="ui-input__title-wrap">
            <span
              class="ui-input__title">
              {{ title }}
            </span>

            <error-tooltip
              v-if="errorMode === 'tooltip'"
              :error="error"
              :z-index="zIndexTooltip"
              :error-triggers="errorTriggers" />
          </div>

          <error-tooltip
            v-else-if="errorMode === 'tooltip'"
            :error="error"
            :z-index="zIndexTooltip"
            :error-triggers="errorTriggers" />
        </div>

        <div
          v-if="textAppendix.suffix && (((title.length && hasValue) || !title.length) || hasFocus)"
          :style="{ transform: `translate(${maximumSuffixPosition}px, -50%)` }"
          class="ui-input__suffix">
          {{ textAppendix.suffix }}
        </div>
      </div>

      <base-tooltip
        v-if="copy"
        :triggers="['manual']"
        :container="tooltipContainer"
        :shown="!!isCopyTooltipShown"
        class="ui-input__tooltip">
        <template #default>
          <span
            class="ui-input__copy"
            :class="[ isAdminStyle ? 'shape-copy--admin' : 'shape-copy' ]"
            @click.stop.prevent="copyToClipboard" />
        </template>

        <template #content>
          Copied to clipboard
        </template>
      </base-tooltip>
    </label>
  </div>
</template>

<script>
import { getTextWidth, throttle } from 'Root/helpers/tools';
import BaseTooltip from 'Components/ui/Tooltip/BaseTooltip.vue';
import ErrorTooltip from 'Components/ui/Tooltip/ErrorTooltip.vue';

const inputNumberBlockedKeys = new RegExp(/[^0-9.-]/g);

export default {
  name: 'UiInput',

  components: {
    ErrorTooltip,
    BaseTooltip,
  },

  props: {
    value: {
      type: [String, Number],
      default: '',
    },

    outerLabel: {
      type: String,
      default: '',
    },

    title: {
      type: String,
      default: '',
    },

    type: {
      type: String,
      default: 'text',
    },

    placeholder: {
      type: String,
      default: '',
    },

    error: {
      type: [String, Boolean],
      default: null,
    },

    errorTriggers: {
      type: Array,
      default: () => (['hover', 'manual', 'click']),
    },

    errorMode: {
      type: String,
      default: 'tooltip',
      validate: (mode) => ['tooltip', 'background'].includes(mode),
    },

    maxlength: {
      type: String,
      default: null,
    },

    regexpFilter: {
      type: RegExp,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    copy: {
      type: Boolean,
      default: false,
    },

    min: {
      type: Number,
      default: null,
    },

    max: {
      type: Number,
      default: null,
    },

    textAppendix: {
      type: Object,
      default: () => ({}),
    },

    tabindex: {
      type: [Number, String],
      default: null,
    },

    inputmode: {
      type: String,
      default: null,
    },

    step: {
      type: String,
      default: null,
    },

    name: {
      type: String,
      default: null,
    },

    autocomplete: {
      type: String,
      default: null,
    },

    accept: {
      type: String,
      default: null,
    },

    icon: {
      type: Object,
      default: () => ({}),
    },

    zIndexTooltip: {
      type: Number,
      default: 100,
    },

    focusOnMount: {
      type: Boolean,
      default: false,
    },

    isCurrency: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      val: this.value,
      hasFocus: false,
      inputWidth: 0,
      throttledWindowResizeHandler: null,
      isCopyTooltipShown: null,
      tooltipContainer: null,
    };
  },

  computed: {
    hasValue() {
      return this.val?.length || typeof this.val === 'number';
    },

    isActive() {
      return !!(this.hasValue || this.placeholder?.length);
    },

    textWidth() {
      if (!this.textAppendix?.suffix || typeof window === 'undefined' || !this.tooltipContainer) {
        return 0;
      }

      const fontSize = getComputedStyle(this.$refs.wrapper)
        .getPropertyValue(this.title.length ? '--font-size--with-title' : '--font-size');

      // Если нужно оперировать размерами в px
      // const fontSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize) * parseFloat(fontSize);

      return getTextWidth(this.val, `500 ${fontSize} Rubik`);
    },

    maximumSuffixPosition() {
      // suffix left: 3px
      const suffixShift = 3;
      return this.textAppendix?.suffix
        ? Math.min(this.textWidth, this.inputWidth - suffixShift)
        : 0;
    },

    isAdminStyle() {
      return this.$route.path.includes('/admin/');
    },

    inputIcon() {
      return {
        path: '',
        width: 16,
        height: 16,
        ...this.icon,
      };
    },
  },

  watch: {
    value: {
      handler(newVal) {
        let currentVal = this.val;

        if (this.type === 'number') {
          currentVal = +currentVal;
          newVal = +newVal;
        }

        if (currentVal !== newVal) {
          this.val = newVal;
        }
      },
    },

    val: {
      handler(newVal) {
        if (this.type === 'number' && Number.isFinite(this.max) && +newVal > this.max) {
          this.val = this.max;
        } else {
          this.emitValue(newVal);
        }
      },
    },
  },

  async mounted() {
    this.tooltipContainer = this.$refs.wrapper;
    if (this.textAppendix?.suffix) {
      this.$nextTick(() => {
        this.inputWidth = this.$refs.input.offsetWidth;
      });
      this.throttledWindowResizeHandler = throttle(() => {
        this.inputWidth = this.$refs.input.offsetWidth;
      }, 50);
      window.addEventListener('resize', this.throttledWindowResizeHandler);
    }

    if (this.focusOnMount) {
      this.focus();
    }
  },

  beforeDestroy() {
    if (this.textAppendix?.suffix) {
      window.removeEventListener('resize', this.throttledWindowResizeHandler);
    }
  },

  methods: {
    setValue(event) {
      if (this.isCurrency) {
        if (/^\d*(\.\d{0,2})?$/.test(event.target.value)) {
          this.val = event.target.value;
          return;
        }

        event.target.value = this.val;
        return;
      }

      this.val = event.target.value;
    },

    focus() {
      this.$refs.input.focus();
    },

    emitValue(value) {
      this.$emit('input', value);
    },

    onFocus(event) {
      this.hasFocus = true;
      this.$emit('focus', event);
    },

    onBlur(event) {
      this.hasFocus = false;
      this.checkMinValue();
      this.$nextTick(() => {
        this.$emit('blur', event);
      });
    },

    onKeyPress(event) {
      if (this.type === 'number' && event.key !== 'Enter' && event.key.match(inputNumberBlockedKeys)) {
        event.preventDefault();
        return;
      }

      const filtered = this.sanitizeValue(event.key);

      if (event.key !== filtered) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      if (this.type === 'number') {
        event.preventDefault();

        const { clipboardData } = event.originalEvent || event;
        const content = clipboardData.getData('text/plain')
          .replace(inputNumberBlockedKeys, '');

        document.execCommand('insertText', false, content);
      }

      setTimeout(() => {
        const filtered = this.sanitizeValue(event.target.value);
        if (event.target.value !== filtered) {
          this.val = filtered;
        }
      }, 0);
    },

    sanitizeValue(value) {
      let filtered = value;

      if (this.regexpFilter) {
        filtered = filtered.replace(this.regexpFilter, '');
      }

      return filtered;
    },

    copyToClipboard() {
      if (navigator.clipboard) {
        // Works in modern browsers at https domains
        navigator.clipboard.writeText(this.val);
        this.showCopyTooltip();
      } else {
        // Fallback
        const input = document.createElement('input');
        input.value = this.val;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.append(input);
        input.focus();
        input.select();
        try {
          document.execCommand('copy');
          this.showCopyTooltip();
        } catch (e) {
          console.log('Unable to copy text. ', e);
        }
        input.remove();
      }
    },

    checkMinValue() {
      if (this.type === 'number' && Number.isFinite(this.min) && (+this.val < this.min)) {
        this.val = this.min;
      }
    },

    showCopyTooltip() {
      clearTimeout(this.isCopyTooltipShown);
      this.isCopyTooltipShown = setTimeout(() => {
        this.isCopyTooltipShown = null;
      }, 2000);
    },

    handleChange(event) {
      this.$emit('input-change', event);
    },
  },
};
</script>

<style lang="scss" scoped>
.ui-input__wrap {
  //--prefix-color: #{$color--basic-white};
  //--prefix-margin: 0 1px 0 0;
  //--border-radius: 3px;
  //--input-min-width: 0;
  --font-weight: 400;
  //--prefix-font-weight: var(--font-weight);
  //--suffix-font-weight: var(--font-weight);
  //--prefix-text-transform: none;
  //--font-size: #{$text-21px};
  //--prefix-font-size: var(--font-size, 21px);
  //--suffix-font-size: var(--font-size, 21px); // if you change this see textWidth computed property
  --font-size--with-title: #{$text-15px};
  --color: #{$color--basic-white};
  //--placeholder-color: #{$color--basic-light-gray};
  --outer-label-font-size: #{$text-11px};
  --outer-label-color: #{$color--basic-light-gray};
  --outer-label-font-weight: 300;
  --outer-label-margin: 8px;
  --outer-label-align: left;
  --opacity: 1;
  --opacity--focused: 1;
  --icon-size: 16px;
  --icon-color: #{$color--basic-white};
  --icon-color--focused: #{$color--basic-white};
  --icon-margin: 0 0 0 0;
  //--height: 60px;
  //--padding: 8px 20px;
  //--background-color: #{$color--additional-menu};
  //--background-color--focused: #{$color--basic-superdark-gray};
  --copy-icon-color: #{$color--basic-white};
}

.ui-input__wrap--error.ui-input__wrap--error { /* Повышаем специфичность, без important */
  --background-color: #{$color--preset-bad};
  --color: #{$color--basic-white};
  --placeholder-color: #{$color--basic-white};
}

.ui-input__wrap-inner {
  position: relative;
  display: flex;
  align-items: center;
  height: var(--height, 60px);
  border-radius: var(--border-radius, 3px);
  background-color: var(--background-color, #{$color--additional-menu});
  padding: var(--padding, 8px 20px);
  opacity: var(--opacity);
  transition: background-color .2s ease-out, opacity .2s ease-out;
  border: var(--border, none);

  /* https://caniuse.com/#search=focus-within */
  &:focus-within {
    opacity: var(--opacity--focused);
    background-color: var(--background-color--focused, #{$color--basic-superdark-gray});

    .ui-input__copy:after {
      --shape-background-color: var(--background-color--focused, #{$color--basic-superdark-gray});
    }
  }
}

.outer-input__label {
  font-size: var(--outer-label-font-size);
  font-weight: var(--outer-label-font-weight);
  color: var(--outer-label-color);
  margin-bottom: var(--outer-label-margin);
  text-align: var(--outer-label-align);
}

.ui-input__title-wrap {
  --error-icon-size: 16px;
  --error-icon-padding: 4px;

  display: flex;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(0, -50%);
  transition: .2s ease-out;
  transition-property: transform;
  height: calc(var(--error-icon-size) + var(--error-icon-padding) * 2);
  z-index: 2;
  cursor: text;
}

.ui-input__tooltip {
  transform: scale(1);
  transition: .2s ease-out;
  transition-property: color, transform, font-size;
}

.ui-input__tooltip {
  margin-left: 12px;
  font-size: $text-14px;
}

.ui-input__title {
  font-size: $text-15px;
  font-weight: 300;
  color: $color--basic-white;
  transition: .2s ease-out;
  transition-property: color;
}

.ui-input__input {
  flex-grow: 1;
  width: 100%;
  min-width: var(--input-min-width);
  background-color: transparent;
  color: var(--color);
  font-size: var(--font-size, 21px);
  font-weight: var(--font-weight);
  font-family: Rubik, sans-serif;
  text-align: var(--text-align, left);
  line-height: 1.2;
  border: none;
  outline: none;
  z-index: 2;
  position: relative;

  &::placeholder {
    transition: var(--transition-color);
    color: var(--placeholder-color, #{$color--basic-light-gray});
    font-weight: 500;
    opacity: 1 !important;
  }

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }

  // тут магия, даже не пытайтесь это понять, но выглядит круто
  &:-webkit-autofill,
  &:-webkit-autofill:hover {
    -webkit-text-fill-color: $color--basic-white !important;
    caret-color: $color--basic-white !important;
    color: $color--basic-white !important;
    background-clip: content-box !important; /* Safari наш любимый */
  }

  &:-webkit-autofill:hover,
  &:-webkit-autofill:empty:not(:focus) {
    transition: box-shadow .2s ease-out; // так делать очень плохо, но меня заставили
  }

  &:-webkit-autofill:focus {
    box-shadow: inset 0 0 0 30px var(--background-color--focused, #{$color--basic-superdark-gray}) !important;
  }

  &:-webkit-autofill:empty:not(:focus) {
    box-shadow: inset 0 0 0 30px var(--background-color, #{$color--additional-menu}) !important;
  }
}

.ui-input__input-wrap {
  position: relative;
}

.ui-input__body, .ui-input__input-wrap {
  display: flex;
  flex: 1;
  align-items: center;
}

.ui-input--has-suffix {
  padding-right: 1ch;
}

.ui-input--has-title {
  align-items: flex-end;

  .ui-input__input {
    padding: 16px 0 0 0;
  }

  .ui-input__input,
  .ui-input__prefix,
  .ui-input__suffix {
    /* The browser will zoom if the font-size is less than 16px */
    font-size: var(--font-size--with-title);
  }

  .ui-input__prefix {
    margin: var(--prefix-margin, 0 1px 0 0);
  }

  .ui-input__suffix {
    top: calc(50% + 9px);
  }
}

.ui-input__input:focus + .ui-input__title-wrap,
.ui-input__title-wrap--active {
  transform: translate(0, -50%) translate(-6.67%, -55%) scale(0.866);

  .ui-input__title {
    color: $color--basic-light-gray;
  }
}

.ui-input__prefix,
.ui-input__suffix {
  font-family: Rubik, sans-serif;
}

.ui-input__prefix {
  position: relative;
  padding: 1px 0;
  margin: var(--prefix-margin, 0 1px 0 0);
  text-transform: var(--prefix-text-transform, none);
  display: flex;
  align-items: center;
  pointer-events: none;
  color: var(--prefix-color, #{$color--basic-white});
  font-size: var(--prefix-font-size, var(--font-size));
  font-weight: var(--prefix-font-weight, var(--font-weight));
  white-space: nowrap;
}

.ui-input__suffix {
  position: absolute;
  top: 50%;
  left: 3px;
  transform: translate(0, -50%);
  pointer-events: none;
  font-size: var(--suffix-font-size, var(--font-size));
  font-weight: var(--suffix-font-weight, var(--font-weight));
  white-space: nowrap;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .2s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.error-baloon {
  width: 100%;
}

.ui-input__copy {
  --shape-color: var(--copy-icon-color);
  --shape-background-color: var(--background-color, #{$color--additional-menu});
}

.ui-input__icon-wrap {
  margin: var(--icon-margin);
  width: var(--icon-size);
  height: var(--icon-size);
}

.ui-input__icon-image {
  --icon-path: '';

  width: 100%;
  height: 100%;
  background-color: var(--icon-color);
  -webkit-mask: var(--icon-path) no-repeat center;
  mask: var(--icon-path) no-repeat center;
  -webkit-mask-size: contain;
  mask-size: contain;
}
</style>
